<template>
  <v-dialog v-model="model" max-width="700" scrollable persistent opacity="0.65">
    <v-card>
      <v-form @submit.prevent="onSubmit">
        <v-card-title class="text-center">
          <h4 v-if="title"> {{ title }}</h4>
          <h4 v-if="type==='confirmation'" class="justify-center"> Confirm</h4>
        </v-card-title>
        <v-divider class="mx-4" />
        <v-card-text>
          <div v-if="type === 'confirmation' || type === 'confirmationTable'" class="swal2-icon swal2-question swal2-icon-show" style="display: flex;">
            <div class="swal2-icon-content">?</div>
          </div>
          <div class="d-flex flex-column align-center">
            <div class="text-h6 text-center">
              {{ question }}
            </div>
          </div>
          <v-text-field
            v-if="type === 'text'"
            v-model="inputValue"
            :label="label"
            v-bind="inputValueAttrs"
          />
          <v-autocomplete
            v-if="type === 'select'"
            v-model="inputValue"
            :label="label"
            :items="items"
            :item-title="itemTitle"
            v-bind="inputValueAttrs"
            return-object
          />
          <div v-if="type==='progress'" class="text-center">
            <v-progress-circular
              :size="50"
              color="primary"
              indeterminate
            />
            <h3>{{ message }}</h3>
          </div>
        </v-card-text>
        <v-card-actions v-if="type!=='progress'" class="d-flex">
          <div class="w-100 pr-1">
            <v-btn block color="error" variant="outlined" @click="cancel">{{ cancelText }}</v-btn>
          </div>
          <div class="w-100 pl-1">
            <v-btn block color="success" type="submit" variant="outlined">{{ confirmText }}</v-btn>
          </div>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { z } from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import type { PromptProperties } from '~/types/Reusable'

const model = ref(false)
const {
  confirmText = 'YES', cancelText = 'CANCEL', question,
  itemTitle, message,
  rules, initialValue,
  id, type,
} = defineProps<PromptProperties<Record<string, never>>>()
const { $eventBus } = useNuxtApp()
const rotateAnimation = ref(false)

onMounted(() => {
  model.value = true
  rotateAnimation.value = true
  setTimeout(() => rotateAnimation.value = false, 1000) // Reset after animation completes
})
const validationSchema = computed(() => {
  return z.object({
    inputValue: rules ? rules : z.string().optional(),
  })
})
const { defineField, handleSubmit, setFieldValue } = useForm({
  validationSchema: toTypedSchema(validationSchema.value),
  initialValues: {
    inputValue: initialValue,
  },
})

const [inputValue, inputValueAttrs] = defineField('inputValue', VUETIFY_INPUT_CONFIG)

const onSubmit = handleSubmit((values) => {
  model.value = false
  $eventBus.emit('hide-prompt', {
    id: id,
    value: type === 'confirmation' ? true : values.inputValue,
  })
})

const cancel = () => {
  model.value = false
  $eventBus.emit('hide-prompt', {
    id: id,
    cancelled: true,
  })
}

watch(
  () => id,
  (_n, _o) => {
    setFieldValue('inputValue', initialValue)
  }
)
</script>
